import useMediaQuery from '@/hooks/use-media-query'
import CompanyService from '@/services/Company/CompanyService'
import OrderService from '@/services/Order/OrderService'
import { BookingSlotType } from '@/types/BookingSlotType'
import { ClientType } from '@/types/ClientType'
import { CompanyType } from '@/types/CompanyType'
import { ProductType } from '@/types/ProductType'
import {
  generateBarcode,
  generateMultiorderCode,
  generateRandomNumber,
} from '@/utils/Functions'
import { flashStore } from '@/utils/Store'
import * as Sentry from '@sentry/react'
import { AxiosError, AxiosResponse } from 'axios'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

export interface OrderCreate {
  ageRestriction?: number | string | null
  barcode?: string
  bookingSlot: string
  _bookingSlot?: BookingSlotType
  client?: {
    email: string
    firstname: string
    lastname: string
    phone: string
  }
  hold?: number
  products: ProductType[]
  receiveCode?: string
  multiOrderCode?: string
  totalSlot?: number
}

const MD_BREAKPOINT = '(min-width: 769px)'

export default function useOrderAdd() {
  /* States / Hooks
   *******************************************************************************************/
  const [companies, setCompanies] = React.useState<CompanyType[]>([])
  const [companySelected, setCompanySelected] = React.useState<CompanyType | undefined>()
  const flash = flashStore()
  const [onSubmitLoading, setOnSubmitLoading] = React.useState<boolean>(false)
  const [orders, setOrders] = React.useState<OrderCreate[]>([])
  const [client, setClient] = React.useState<ClientType>()
  const [stepCreation, setStepCreation] = React.useState<number>(1)
  const { register, handleSubmit, reset } = useForm<ClientType>()
  const navigate = useNavigate()
  const isDesktop = useMediaQuery(MD_BREAKPOINT)

  /* UseEffect
   *******************************************************************************************/
  React.useEffect(() => {
    getCompanies()
  }, [])

  /* Functions
   *******************************************************************************************/
  // OK - Lister toutes les sociétés
  const getCompanies = () => {
    CompanyService.getBookingSlotsOfCompanies(1, 1000).then((response: AxiosResponse) => {
      setCompanies(response.data['hydra:member'])
    })
  }

  // OK - A la sélection d'une société lister tous ces bookings
  const handleCompanySelected = (company: CompanyType) => {
    setCompanySelected(company)
    setStepCreation(2)
  }

  // OK - ClearState rollback to step 1
  const handleClearState = () => {
    setCompanySelected(undefined)
    setOrders([])
    setStepCreation(1)
  }

  // OK - Sauvegarder le nombre de casier réservé par booking
  const handleSaveNbSlot = (nb_slot: number, currentBooking: BookingSlotType) => {
    if (currentBooking?.available) {
      if (nb_slot > currentBooking?.available) {
        flash.setMessage(
          'error',
          'Attention ! il en reste que ' +
            currentBooking?.available +
            ' casier pour ce booking.'
        )
      } else {
        // Set First Data Orders
        const firstData = {
          bookingSlot: '/api/booking_slots/' + currentBooking?.id,
          _bookingSlot: currentBooking,
          products: [],
          totalSlot: nb_slot,
        }
        setOrders([...orders, { ...firstData }])
      }
    }
  }

  // OK - Submit Nb casier by bookings
  const handleSumbitNbSlot = () => {
    // Set New Data Format Orders
    const new_orders = orders.reduce<OrderCreate[]>((acc, item) => {
      const numSlots = item.totalSlot || 1
      for (let i = 0; i < numSlots; i++) {
        acc.push({
          ...item,
          totalSlot: 1,
        })
      }
      return acc
    }, [])

    setOrders([])
    setOrders(new_orders)
    setStepCreation(3)
  }

  // OK - Submit for preparing order
  const handlePrepareOrder = (customer: ClientType) => {
    setClient(customer)
    let orderMain = null
    const receiveCode = generateRandomNumber()
    const barcode = companySelected?.cleveronCompanyId + '-' + generateBarcode()
    const _orders = orders.map((order: OrderCreate, index: number) => ({
      ...order,
      ...{ client: customer },
      ...{
        receiveCode: receiveCode,
        barcode: orders.length > 1 ? barcode + (index + 1) : barcode,
        hold: 2880,
      },
    }))
    if (_orders.length > 1) {
      const multiOrderCode = generateMultiorderCode()
      const multi_orders = _orders.map((_order: OrderCreate) => ({
        ..._order,
        multiOrderCode,
      }))
      orderMain = multi_orders
    } else {
      orderMain = _orders
    }

    setClient(customer)
    setOrders(orderMain)
    setStepCreation(4)
  }

  // OK - Set Products by Carts
  const handleSetProducts = (index: number, products: ProductType[]) => {
    const newOrders = [...orders]
    newOrders[index].products = products
    setOrders(newOrders)
  }

  // OK - Remove Product by Line
  const handleRemoveProductLine = (index: number, productIndex: number) => {
    const updatedOrders = [...orders]
    updatedOrders[index].products = updatedOrders[index].products.filter(
      (_, i) => i !== productIndex
    )
    setOrders(updatedOrders)
  }

  // Ok - Save Order
  const handleSaveOrder = () => {
    setOnSubmitLoading(true)
    // SetAlcohol Orders
    const updatedOrders = orders.map((order) => {
      const updatedProducts = order.products.map((product) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isAlcoholic, ...rest } = product // Retirez le champ isAlcoholic
        return rest
      })

      // Retirez _bookingSlot de l'objet order
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _bookingSlot, ...orderWithoutBookingSlot } = order

      const hasAlcoholicProduct = order.products.some((product) => product.isAlcoholic)

      if (hasAlcoholicProduct) {
        return {
          ...orderWithoutBookingSlot,
          ageRestriction: 18,
          products: updatedProducts, // Utilisez les produits mis à jour
        }
      } else {
        return {
          ...orderWithoutBookingSlot,
          ageRestriction: null,
          products: updatedProducts, // Utilisez les produits mis à jour
        }
      }
    })

    Promise.all(
      updatedOrders.map((_order) => {
        return OrderService.create(_order)
          .then((response: AxiosResponse) => {
            // Peut-être effectuer d'autres actions en fonction de la réponse
            return response // Passer la réponse à la chaîne de promesses
          })
          .catch((error: AxiosError) => {
            flash.setMessageErrorForm()
            Sentry.captureException(error)
            throw error // Propager l'erreur pour être capturée par le gestionnaire d'erreurs global
          })
      })
    )
      .then(() => {
        navigate('/livraisons')
      })
      .catch((error: AxiosError) => {
        console.error('Une ou plusieurs requêtes de création ont échoué:', error)
        // Peut-être afficher un message d'erreur à l'utilisateur
      })
      .finally(() => {
        setOnSubmitLoading(false)
      })
  }

  return {
    //states
    companies,
    setCompanies,
    companySelected,
    setCompanySelected,
    flash,
    onSubmitLoading,
    setOnSubmitLoading,
    orders,
    setOrders,
    client,
    setClient,
    stepCreation,
    setStepCreation,
    register,
    handleSubmit,
    reset,
    isDesktop,
    //functions
    handleCompanySelected,
    handleClearState,
    handleSaveNbSlot,
    handleSumbitNbSlot,
    handlePrepareOrder,
    handleSetProducts,
    handleRemoveProductLine,
    handleSaveOrder,
  }
}
